/**
 * AuthScreen — 登入/註冊畫面
 *
 * 簡潔的表單，風格與現有 MainMenu 一致（深色背景、金色調）。
 */

import { useState, type SyntheticEvent } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { useDeckStore } from '../../store/deckStore';
import { getWSClient } from '../../services/NetworkManager';
import { startListening } from '../../services/GameEventHandler';

type AuthMode = 'login' | 'register';

export function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const { isLoading, error, login, register, clearError } = useAuthStore();
  const { navigateTo } = useUIStore();

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    const action = mode === 'login' ? login : register;
    action(username, password)
      .then(() => {
        const state = useAuthStore.getState();
        if (state.isLoggedIn && state.token) {
          // 登入成功，建立 WebSocket 連線
          const wsClient = getWSClient();
          wsClient.connect(state.token);
          startListening();
          // 載入伺服器牌組
          void useDeckStore.getState().fetchDecks();
          // 導航到大廳
          navigateTo('lobby');
        }
      })
      .catch(() => {
        // 錯誤已由 store 處理
      });
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    clearError();
  };

  const isFormValid =
    username.length >= 2 && username.length <= 32 && password.length >= 6;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>星靈王</h1>
      <p style={styles.subtitle}>
        {mode === 'login' ? '歡迎回來' : '建立新帳號'}
      </p>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>帳號</label>
          <input
            type="text"
            value={username}
            onChange={(e) => { setUsername(e.target.value); }}
            placeholder="2-32 個字元"
            style={styles.input}
            autoFocus
            disabled={isLoading}
            maxLength={32}
            minLength={2}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>密碼</label>
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); }}
            placeholder="至少 6 個字元"
            style={styles.input}
            disabled={isLoading}
            minLength={6}
          />
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button
          type="submit"
          style={{
            ...styles.submitButton,
            opacity: isFormValid && !isLoading ? 1 : 0.5,
          }}
          disabled={!isFormValid || isLoading}
        >
          {isLoading
            ? '處理中...'
            : mode === 'login'
              ? '登入'
              : '註冊'}
        </button>
      </form>

      <button style={styles.toggleButton} onClick={toggleMode} disabled={isLoading}>
        {mode === 'login'
          ? '還沒有帳號？點此註冊'
          : '已有帳號？點此登入'}
      </button>

      {/* 單機模式入口 */}
      <button
        style={styles.offlineButton}
        onClick={() => { navigateTo('mainMenu'); }}
        disabled={isLoading}
      >
        單機模式（不需登入）
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a2e',
    fontFamily: 'Arial, sans-serif',
  },
  title: {
    color: '#e8d282',
    fontSize: '56px',
    margin: 0,
    textShadow: '0 0 20px rgba(232, 210, 130, 0.4)',
  },
  subtitle: {
    color: '#888888',
    fontSize: '18px',
    margin: '8px 0 32px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '320px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    color: '#aaaaaa',
    fontSize: '14px',
  },
  input: {
    padding: '12px 16px',
    fontSize: '16px',
    backgroundColor: '#2a2a4a',
    color: '#ffffff',
    border: '1px solid #3a3a5a',
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'Arial, sans-serif',
  },
  error: {
    color: '#ff6666',
    fontSize: '14px',
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 102, 102, 0.1)',
    borderRadius: '4px',
    textAlign: 'center' as const,
  },
  submitButton: {
    padding: '14px',
    fontSize: '18px',
    backgroundColor: '#e8d282',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
    marginTop: '8px',
  },
  toggleButton: {
    marginTop: '16px',
    padding: '8px',
    fontSize: '14px',
    backgroundColor: 'transparent',
    color: '#6688aa',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },
  offlineButton: {
    marginTop: '24px',
    padding: '10px 24px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#888888',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },
};
